import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { BasicError } from 'projects/shared/lib/error/basic-error';
import { IAddressService, IAddressServiceToken } from 'projects/shared/lib/services/data/address/address-service.interface';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { AddressDto, AddressIdentifierDto, CountryDto, EAddressType } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { firstValueFrom } from 'rxjs';
import { CountryComboBoxComponent } from '../../country-combo-box/country-combo-box.component';
import { DirectivesModule } from 'projects/shared/lib/directives/directives.module';

@Component({
  selector: 'app-address-add-dialog',
  imports: [
    FormsModule,
    CountryComboBoxComponent,
    DirectivesModule
  ],
  templateUrl: './address-add-dialog.component.html',
  styleUrls: ['./address-add-dialog.component.scss']
})
export class AddressAddDialogComponent implements OnInit {
  private readonly logger: LoggerService;
  private readonly addressService: IAddressService;

  @Input() public modalTitle?: string;
  @Input() public addressTemplate?: AddressDto;
  @Input() public submitButtonLabel: string;
  @Input() public cancelButtonLabel: string;
  @Output() public modalSubmit: EventEmitter<AddressDto> = new EventEmitter<AddressDto>();
  @Output() public modalCancel: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

  public address: AddressDto;
  public isTemplateSelected: boolean;
  public countryList: CountryDto[];
  public selectedCountry: CountryDto | undefined;

  public constructor() {
    this.logger = inject(LoggerService);
    this.addressService = inject(IAddressServiceToken);

    this.submitButtonLabel = $localize`:@@DialogAddressAddSubmitButtonDefaultLabel:Save`;
    this.cancelButtonLabel = $localize`:@@DialogAddressAddCancelButtonDefaultLabel:Cancel`;
    this.address = new AddressDto({
      addressIdentifier: new AddressIdentifierDto({
        addressNumber: undefined,
        addressType: EAddressType.DifferentAddress
      }),
      city: '',
      countryCode: '',
      name1: '',
      name2: '',
      name3: '',
      street: '',
      zipCode: '',
    });
    this.countryList = [];
    this.isTemplateSelected = false;
  }

  public async ngOnInit(): Promise<void> {
    if (this.addressTemplate) {
      Object.assign(this.address, this.addressTemplate);
      this.address.addressIdentifier = new AddressIdentifierDto({
        addressType: EAddressType.DifferentAddress,
        addressNumber: undefined,
      });
    }
    await this.addressCountryListLoad();
  }

  public submit() {
    // validation
    let form = document.getElementById('addressAddDialog') as HTMLFormElement;
    if (form.checkValidity() === false) {
      form.classList.add('was-validated');
      return;
    }

    // address.countryCode is optional by model for 1 project but otherwise required. -> ensure it programatically
    if (this.selectedCountry?.countryCode == undefined || this.selectedCountry?.countryCode == '') {
      form.classList.add('was-validated');
      return;
    }

    // valid
    this.modalSubmit.emit(this.address);
  }

  public cancel(event: MouseEvent) {
    this.modalCancel.emit(event);
  }

  private async addressCountryListLoad() {
    await firstValueFrom(this.addressService.countryListGet(true)).then((result) => {
      this.logger.trace("addressCountryListLoad has been called", this.constructor.name);
      let countryList = result.countryList;
      if (!countryList || countryList.length < 1) {
        return;
      }

      this.countryList = countryList;
    }).catch((error) => {
      this.logger.error(error, this.constructor.name);
      let errorMessage = $localize`:@@AddressAddDialogCountryLoadErrorMessage:Could not load countrys`;
      throw new BasicError(errorMessage, JSON.parse(error.response).errorCode);
    });
  }

  public onCountryCodeChange(value: CountryDto) {
    this.logger.trace("onCountryCodeChange has been called", this.constructor.name);
    this.selectedCountry = value;
    this.address.countryCode = this.selectedCountry?.countryCode;
  }
}
